/**
 * Section Component for Lobby2.0
 * PERFORMANCE OPTIMIZED: Uses CSS modules for instant view switching
 */

import type { FC } from 'react';
import { memo, useMemo, useCallback } from 'react';

import { BackgroundService } from '../../core/services/managers/BackgroundManager';
import SectionGridRenderer from './components/SectionGridRenderer';
import { DefaultSectionHeader } from './SectionHeader';
import { JackpotPanel } from 'features/jackpots';
import { TopWinGamesSection } from 'features/topWins';
import { HotColdSwitchPanel, useHotColdToggle } from 'features/hotCold';

import { useScrollDetails } from './hooks';
import { setSelectedSection, useViewMode, setViewMode, useAssetsConfig } from '../../store/app/appStore';
import { GAME_FEATURES, HOT_AND_COLD_SECTION_NAME, SECTION_NAMES } from '@repo/common';
import type { ISectionProps } from '../../types/models/ISectionProps';
import { StyledSectionBackground } from './StyledSectionBackground';
import { CampaignMultiViewSection, useCampaignSectionGate } from '../../features/campaigns/_exports/ui';

import { JackpotFallback } from '../../shared/components/ErrorBoundaries/JackpotPanelErrorBoundary/JackpotPanelFallback';
import { GenericErrorBoundary } from '../../shared/components/ErrorBoundaries/GenericErrorBoundary';
import { useGameSounds } from '../../shared/hooks';
import { sectionNavigationTracker } from '../../core/services/SectionNavigationTracker';

import styles from './Section.module.css';

const Section: FC<ISectionProps> = ({
  data,
  isActive = false,
  isPortrait,
  customHeader,
  isFav = false,
  lobbyContainerRef,
}) => {
  const viewMode = useViewMode();
  const { isCampaignEnabled } = useCampaignSectionGate();
  const { isColdSelected, setIsColdSelected, displayGames, backgroundSourceName } = useHotColdToggle(data);

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  const {
    gridContainerRef,
    scrollArrows,
    scrollLeft,
    scrollRight,
    handleMouseEnter,
    handleMouseLeave,
  } = useScrollDetails(isActive, data.name, displayGames.length, lobbyContainerRef);

  // On Hot/Cold toggle, reset the horizontal scroll to 0 so the new side starts
  // at the beginning instead of inheriting the other side's position. Done inline
  // on the user click — not in an effect — so it stays synchronous with intent
  // and doesn't fire on mount.
  //
  // gridContainerRef points at ReactVirtualGrid's OUTER wrapper, which is not the
  // scroller. The actual scroll element is the inner div tagged with
  // data-grid-scroll-container in ReactVirtualGrid.tsx.
  const handleHotColdToggle = useCallback((next: boolean) => {
    setIsColdSelected(next);
    if (data.name !== HOT_AND_COLD_SECTION_NAME) return;
    const scrollEl = gridContainerRef.current?.querySelector<HTMLElement>('[data-grid-scroll-container]');
    if (scrollEl) scrollEl.scrollLeft = 0;
  }, [setIsColdSelected, data.name, gridContainerRef]);

  const { assetsHost, assetsHash } = useAssetsConfig();
  const { playSelectTabSound } = useGameSounds();

  // Background settings
  //TODO Refactor BackgroundService!
  const backgroundSettings = useMemo(() => {
    return BackgroundService.getBackgroundSettings(
      backgroundSourceName,
      viewMode,
      isPortrait,
      assetsHost,
      assetsHash
    );
  }, [backgroundSourceName, viewMode, assetsHost, assetsHash, isPortrait]);

  const jackpotPanelName = useMemo(() => {
    return data.name === SECTION_NAMES.HIGH_RTP
      ? GAME_FEATURES.HIGH_RTP
      : data.jackpotName;
  }, [data.name, data.jackpotName]);

  const isInSingleView = viewMode === 'single';

  // Determine if jackpot panel should be shown
  const hasJackpotPanel = !!jackpotPanelName;

  // PERFORMANCE: Memoized callback to prevent recreation
  const handleViewToggle = useCallback(() => {
    // used only in multi view
    if (!isInSingleView && !isActive) {
      playSelectTabSound();

      // Go to single-view for this section
      sectionNavigationTracker.trackSectionNavigation(data.name, 'all_link');
      setSelectedSection(data.name);
      setViewMode('single');
    }
  }, [isInSingleView, isActive, data.name, setSelectedSection, setViewMode]);

  // PERFORMANCE: No CSS class calculation - use data attributes!
  const containerProps = useMemo(
    () => ({
      'data-view': viewMode,
      'data-active': isActive ? 'true' : 'false',
      'data-section': data.name,
      'data-has-background': backgroundSettings.hasBackground ? 'true' : 'false',
    }),
    [viewMode, isActive, data.name, backgroundSettings.hasBackground],
  );

  // Handle campaign sections (Tournaments, Prize Pop) using the unified MultiViewCampaign wrapper
  // Strategy pattern resolves the correct behavior based on section name
  if (isCampaignEnabled(data.name) === true) {
    return <CampaignMultiViewSection data={data} isActive={isActive} />;
  }

  if (data.name === SECTION_NAMES.TOP_WIN_GAMES) {
    return (
      <TopWinGamesSection data={data} isActive={isActive} containerProps={containerProps}/>
    );
  }

  return (
    <div className={styles.sectionContainer} {...containerProps}>
      {customHeader ? (
        customHeader
      ) : (
        <DefaultSectionHeader
          name={data.name}
          isActive={isActive}
          handleViewToggle={handleViewToggle}
          gamesCount={displayGames?.length}
        />
      )}
      {/* Section Body - Contains games and optional jackpot panel */}
      <StyledSectionBackground
        className={styles.sectionBody}
        data-has-jackpot={hasJackpotPanel}
        data-view={viewMode}
        data-has-background={backgroundSettings.hasBackground}
        $backgroundSettings={backgroundSettings}
      >
        {/* Render switch panel for Hot & Cold section */}
        {data.name === HOT_AND_COLD_SECTION_NAME && (
          <HotColdSwitchPanel isColdSelected={isColdSelected} onToggle={handleHotColdToggle} />
        )}
        {/* Jackpot Panel */}
        {jackpotPanelName && (
          <GenericErrorBoundary
            type="jackpot"
            identifier={`Jackpot-${jackpotPanelName}`}
            fallback={(error, reset) => (
              <JackpotFallback jackpotName={jackpotPanelName} error={error} onRetry={reset} />
            )}
          >
            <JackpotPanel jackpotName={jackpotPanelName} sectionName={backgroundSourceName} games={displayGames} />
          </GenericErrorBoundary>
        )}
        <SectionGridRenderer
          isInSingleView={isInSingleView}
          games={displayGames}
          isFav={isFav}
          sectionName={backgroundSourceName}
          hasJackpotPanel={hasJackpotPanel}
          lobbyContainerRef={lobbyContainerRef}
          scrollArrows={scrollArrows}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
          scrollLeft={scrollLeft}
          scrollRight={scrollRight}
          ref={gridContainerRef}
        />
      </StyledSectionBackground>
    </div>
  );
};

// PERFORMANCE OPTIMIZED: Memoized to prevent re-renders when section data hasn't
// changed. Uses reference equality on `data` — in production, the sections array
// and its entries are stable between API responses, so this is a no-op most of
// the time. When something actually mutates a section (e.g. the QA Panel's Mini
// Backoffice editing a game field), the controller spreads into a new section
// object, this comparator sees the new reference, and Section re-renders.
//
// The previous version compared `data.games.map(g => g.gameKey).join(',')`
// which made the false assumption that "same keys in same order" implies "same
// content" — that's only true when game fields are immutable, which breaks the
// moment anything writes to a game at runtime.
export default memo(Section, (prevProps, nextProps) => {
  if (prevProps.data !== nextProps.data) return false;
  if (prevProps.isActive !== nextProps.isActive) return false;
  if (prevProps.isPortrait !== nextProps.isPortrait) return false;
  if (prevProps.isFav !== nextProps.isFav) return false;
  return true;
});
